import { useCallback, useMemo, useState } from 'react'
import {
  AlertTriangle, ArrowLeftRight, Check, Copy, Link2, Plus, RefreshCw, Trash2,
} from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import type { IcalFeed } from '../data/model'
import { assertWrote } from '../data/supabaseAdapter'
import { Badge, Button, Card, Field, Input, Select } from '../ui/kit'
import { oeffentlicheUrl } from '../lib/urls'

/**
 * Portal calendars, both directions, in one card.
 *
 * The mental model a landlord already has is "Booking.com and my calendar have to
 * agree", and it has two halves that behave nothing alike:
 *
 *   IMPORT — a URL the portal gives us. We poll it every 30 minutes, and what comes
 *            back lands in `ical_events`, NEXT to the bookings and never inside them.
 *            An imported block can therefore contradict a stay sold here, and when it
 *            does the calendar says so instead of quietly picking a winner.
 *   EXPORT — a URL we give the portal. One per unit, authorised by a random token and
 *            nothing else, carrying dates and the word "Belegt" — no guest names, no
 *            prices. Rotating the token revokes the link.
 *
 * Texts are inline rather than in `locales/`, following ComplianceCards: this is a
 * settings card with a lot of prose and a short life expectancy in its current shape,
 * and putting forty keys into the shared catalogue for it would cost more than it
 * saves.
 *
 * All state comes from `store.db.ical_feeds`; every write goes straight to Supabase
 * and then asks the store to reload. Two sources of truth for the same six rows is
 * how the last stale-list bug happened.
 */

/**
 * Die Adresse, die der Vermieter bei Booking.com oder Airbnb einträgt.
 *
 * Sie läuft über die eigene Domain und nicht direkt auf Supabase. Das ist keine
 * Kosmetik: der Vermieter kopiert diesen Link in ein fremdes Portal und schickt ihn
 * manchmal seinem Steuerberater oder seiner Vertretung. Eine Adresse aus zufälligen
 * Buchstaben mit dem Namen eines Dienstes, von dem er nie gehört hat, sieht aus wie
 * etwas, das man nicht anklicken sollte — und genau so hat er sich gemeldet.
 *
 * Der Umweg ist eine Weiterleitung in `public/_redirects` und kostet nichts. Er
 * bringt nebenbei, dass die Feed-Adressen gleich bleiben, falls das Backend je
 * umzieht; die Portale merken sich diese URL für Jahre.
 *
 * Die Endung `.ics` steht dran, weil manche Portale eine Adresse ohne sie gar nicht
 * erst annehmen.
 */
function exportUrl(token: string): string {
  return oeffentlicheUrl(`/ical/${token}.ics`)
}

/** 24 random bytes as hex — the same shape gen_random_bytes(24) produces server-side. */
function newToken(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * What went wrong, in a sentence a landlord can act on.
 *
 * The Edge Function stores a stable code, sometimes with a colon and the raw detail
 * behind it. Storing the German sentence instead would have been simpler and wrong:
 * the same row is read by an English-speaking user, and a message frozen at write
 * time cannot follow the reader. Unknown codes fall through verbatim rather than
 * becoming "an error occurred", because a raw string a landlord can paste into a
 * support mail beats a polite one that says nothing.
 */
function explainError(raw: string, de: boolean): string {
  const [code = '', ...rest] = raw.split(': ')
  const detail = rest.join(': ')

  const httpStatus = /^http_(\d{3})$/.exec(code)?.[1]
  if (httpStatus) {
    if (httpStatus === '401' || httpStatus === '403') {
      return de
        ? `Das Portal hat den Zugriff abgelehnt (${httpStatus}). Der Link ist vermutlich abgelaufen — im Portal einen neuen erzeugen.`
        : `The portal refused access (${httpStatus}). The link has probably expired — generate a new one in the portal.`
    }
    if (httpStatus === '404') {
      return de
        ? 'Unter dieser Adresse liegt kein Kalender (404). Bitte den Link im Portal noch einmal kopieren.'
        : 'There is no calendar at that address (404). Please copy the link from the portal again.'
    }
    return de
      ? `Das Portal hat mit Fehler ${httpStatus} geantwortet. Meist vorübergehend — der nächste Lauf versucht es erneut.`
      : `The portal answered with error ${httpStatus}. Usually temporary — the next run tries again.`
  }

  switch (code) {
    case 'url_missing':
      return de ? 'Für diesen Feed ist keine Adresse hinterlegt.' : 'No address is stored for this feed.'
    case 'url_invalid':
      return de
        ? 'Die Adresse ist keine gültige URL. Sie muss mit https:// oder webcal:// beginnen.'
        : 'That address is not a valid URL. It has to start with https:// or webcal://.'
    case 'url_not_allowed':
      return de
        ? 'Diese Adresse zeigt nicht ins Internet und wird aus Sicherheitsgründen nicht abgerufen.'
        : 'That address does not point to the internet and is refused for security reasons.'
    case 'timeout':
      return de
        ? 'Das Portal hat nicht innerhalb von 20 Sekunden geantwortet.'
        : 'The portal did not answer within 20 seconds.'
    case 'too_large':
      return de
        ? 'Der Kalender ist ungewöhnlich groß und wurde nicht verarbeitet.'
        : 'That calendar is unusually large and was not processed.'
    case 'not_a_calendar':
      return de
        ? 'Die Antwort ist kein Kalender. Oft ist es eine Anmeldeseite — dann ist der Link nicht der öffentliche.'
        : 'The response is not a calendar. Often it is a login page, which means the link is not the public one.'
    case 'db_read_failed':
    case 'db_write_failed':
      return de
        ? `Die Daten konnten nicht gespeichert werden. ${detail}`
        : `The data could not be saved. ${detail}`
    default:
      return raw
  }
}

function whenText(value: string | null | undefined, de: boolean): string {
  if (!value) return de ? 'noch nie' : 'never'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return new Intl.DateTimeFormat(de ? 'de-AT' : 'en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(d)
}

function CopyButton({ value, de }: { value: string; de: boolean }) {
  const [done, setDone] = useState(false)
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={async () => {
        try { await navigator.clipboard.writeText(value) } catch { /* nothing to do */ }
        setDone(true)
        setTimeout(() => setDone(false), 1500)
      }}
    >
      {done ? <Check size={14} /> : <Copy size={14} />}
      {done ? (de ? 'Kopiert' : 'Copied') : (de ? 'Kopieren' : 'Copy')}
    </Button>
  )
}

export default function IcalCard() {
  const { lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [draftUnit, setDraftUnit] = useState('')
  const [draftLabel, setDraftLabel] = useState('')
  const [draftUrl, setDraftUrl] = useState('')

  const units = useMemo(
    () => store.db.units.filter((u) => u.active !== false),
    [store.db.units],
  )
  const feeds = store.db.ical_feeds
  const imports = feeds.filter((f) => f.direction === 'import')
  const exports_ = feeds.filter((f) => f.direction === 'export')

  const run = useCallback(async (work: () => Promise<void>) => {
    setBusy(true); setError(null); setNote(null)
    try {
      await work()
      store.reload()
    } catch (e) {
      setError((e as Error).message || 'unknown')
    }
    setBusy(false)
  }, [store])

  const addImport = () => run(async () => {
    if (!supabase) throw new Error(de ? 'Kein Server verbunden.' : 'No backend connected.')
    const unitId = draftUnit || units[0]?.id
    if (!unitId) throw new Error(de ? 'Kein Objekt vorhanden.' : 'No booking object yet.')
    const url = draftUrl.trim()
    if (!/^(https?|webcal):\/\//i.test(url)) {
      throw new Error(de
        ? 'Die Adresse muss mit https:// oder webcal:// beginnen.'
        : 'The address has to start with https:// or webcal://.')
    }
    const { error: err } = await supabase.from('ical_feeds').insert({
      tenant_id: store.db.tenant.id,
      unit_id: unitId,
      direction: 'import',
      label: draftLabel.trim() || (de ? 'Portal' : 'Portal'),
      url,
      active: true,
    })
    if (err) throw new Error(err.message)
    setDraftLabel(''); setDraftUrl(''); setAdding(false)
  })

  const removeFeed = (feed: IcalFeed) => {
    const isExport = feed.direction === 'export'
    const ok = window.confirm(isExport
      ? (de
        ? 'Export-Link löschen? Das Portal, das ihn abruft, sieht danach keine Belegung mehr.'
        : 'Delete this export link? The portal reading it will no longer see any occupancy.')
      : (de
        ? 'Feed entfernen? Die bereits eingelesenen Fremdbelegungen verschwinden mit ihm.'
        : 'Remove this feed? The foreign blocks it imported disappear with it.'))
    if (!ok) return
    void run(async () => {
      if (!supabase) throw new Error(de ? 'Kein Server verbunden.' : 'No backend connected.')
      // Deutsch wie alle Schreibfehler dieser Anwendung — die Begründung in
      // `assertWrote` ist es auch. Siehe die Notiz zur Übersetzung im Änderungsbericht.
      assertWrote(
        await supabase.from('ical_feeds').delete().eq('id', feed.id).select('id'),
        'Feed entfernen',
      )
    })
  }

  const createExport = (unitId: string, label: string) => run(async () => {
    if (!supabase) throw new Error(de ? 'Kein Server verbunden.' : 'No backend connected.')
    const { error: err } = await supabase.from('ical_feeds').insert({
      tenant_id: store.db.tenant.id,
      unit_id: unitId,
      direction: 'export',
      label,
      token: newToken(),
      active: true,
    })
    if (err) throw new Error(err.message)
  })

  const rotate = (feed: IcalFeed) => {
    if (!window.confirm(de
      ? 'Neuen Link erzeugen? Der alte funktioniert sofort nicht mehr und muss in jedem Portal ersetzt werden.'
      : 'Generate a new link? The old one stops working immediately and has to be replaced in every portal.')) return
    void run(async () => {
      if (!supabase) throw new Error(de ? 'Kein Server verbunden.' : 'No backend connected.')
      /*
       * Der still verworfene Fall ist hier der gefährlichste im ganzen Bündel: der
       * Vermieter erneuert den Link, WEIL der alte in falsche Hände geraten ist, und
       * geht danach davon aus, dass er tot ist. Trifft das Update null Zeilen, gilt
       * der alte Token weiter — und wer ihn hat, liest die Belegung weiter mit.
       */
      assertWrote(
        await supabase.from('ical_feeds')
          .update({ token: newToken() }).eq('id', feed.id).select('id'),
        'Link erneuern',
      )
    })
  }

  const syncNow = () => run(async () => {
    if (!supabase) throw new Error(de ? 'Kein Server verbunden.' : 'No backend connected.')
    const { data, error: err } = await supabase.functions.invoke('ical-sync', { body: {} })
    if (err) {
      // supabase-js reports every transport failure as "Failed to send a request to
      // the Edge Function", which tells a landlord nothing. The cause is almost
      // always that the service is briefly down; the raw text stays attached so a
      // support mail carries something an engineer can use.
      throw new Error(de
        ? `Der Abgleich ließ sich nicht starten. Der automatische Lauf versucht es in spätestens 30 Minuten erneut. (${err.message})`
        : `The sync could not be started. The automatic run tries again within 30 minutes. (${err.message})`)
    }
    const result = data as { feeds?: number; failed?: number } | null
    const total = result?.feeds ?? 0
    const failed = result?.failed ?? 0
    setNote(de
      ? `${total} Feed(s) abgeglichen${failed ? `, ${failed} mit Fehler` : ''}.`
      : `${total} feed(s) synced${failed ? `, ${failed} with an error` : ''}.`)
  })

  /* --------------------------------------------------------------- demo */

  if (!store.live) {
    return (
      <Card className="p-4 space-y-2">
        <h2 className="font-medium flex items-center gap-2">
          <ArrowLeftRight size={18} /> {de ? 'Portalkalender (iCal)' : 'Portal calendars (iCal)'}
        </h2>
        <p className="text-sm text-muted">
          {de
            ? 'Belegungen von Booking.com, Airbnb und Co. einlesen und den eigenen Kalender bereitstellen. Braucht ein echtes Konto — in der Demo gibt es keine Portale, mit denen sich etwas abgleichen ließe.'
            : 'Read occupancy from Booking.com, Airbnb and friends, and publish your own calendar. Needs a real account — the demo has no portals to sync with.'}
        </p>
        {feeds.length > 0 && (
          <div className="pt-1">
            {feeds.map((f) => (
              <div key={f.id} className="flex items-center gap-3 text-sm py-1">
                <span className="min-w-0 flex-1 truncate">{f.label}</span>
                <span className="text-xs text-muted">{store.unitById(f.unit_id)?.name}</span>
                <Badge tone="neutral">
                  {f.direction === 'import' ? (de ? 'Einlesen' : 'Import') : (de ? 'Bereitstellen' : 'Export')}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    )
  }

  /* --------------------------------------------------------------- live */

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-start gap-2">
        <h2 className="font-medium flex items-center gap-2 flex-1">
          <ArrowLeftRight size={18} /> {de ? 'Portalkalender (iCal)' : 'Portal calendars (iCal)'}
        </h2>
        <Button size="sm" disabled={busy} onClick={syncNow}>
          <RefreshCw size={14} /> {de ? 'Jetzt abgleichen' : 'Sync now'}
        </Button>
      </div>

      <p className="text-sm text-muted">
        {de
          ? 'Eingelesene Fremdbelegungen stehen neben Ihren Buchungen, nicht darin — eine Überschneidung wird im Kalender angezeigt und überschreibt niemals eine eigene Buchung. Der Abgleich läuft automatisch alle 30 Minuten.'
          : 'Imported foreign blocks sit beside your bookings, never inside them — an overlap is shown in the calendar and never overwrites a booking of your own. The sync runs automatically every 30 minutes.'}
      </p>

      {note && <p className="text-sm bg-ok-soft text-ok rounded-xl px-3 py-2">{note}</p>}
      {error && <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2">{error}</p>}

      {/* ------------------------------------------------------- import */}

      <section className="space-y-2">
        <h3 className="text-sm font-medium">
          {de ? 'Einlesen' : 'Import'}
          <span className="text-muted font-normal"> · {imports.length}</span>
        </h3>

        {imports.length === 0 && (
          <p className="text-xs text-muted">
            {de
              ? 'Noch kein Portalkalender hinterlegt. Im Portal den iCal-Export-Link kopieren und hier einfügen.'
              : 'No portal calendar yet. Copy the iCal export link from the portal and paste it here.'}
          </p>
        )}

        {imports.map((f) => (
          <div key={f.id} className="rounded-xl bg-surface-2 p-3 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{f.label}</span>
              <span className="text-xs text-muted truncate">{store.unitById(f.unit_id)?.name}</span>
              <Badge tone={f.last_status === 'error' ? 'danger' : f.last_status === 'ok' ? 'ok' : 'neutral'}>
                {f.last_status === 'error'
                  ? (de ? 'Fehler' : 'Error')
                  : f.last_status === 'ok'
                    ? (de ? `${f.event_count} Termine` : `${f.event_count} events`)
                    : (de ? 'ungeprüft' : 'not run yet')}
              </Badge>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => removeFeed(f)}>
                <Trash2 size={14} />
              </Button>
            </div>
            <p className="text-xs text-muted break-all">{f.url}</p>
            <p className="text-xs text-muted">
              {de ? 'Zuletzt abgeglichen: ' : 'Last synced: '}{whenText(f.last_sync_at, de)}
            </p>
            {f.last_status === 'error' && f.last_error && (
              <p className="text-xs bg-danger-soft text-danger rounded-lg px-2 py-1.5 flex gap-1.5">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>{explainError(f.last_error, de)}</span>
              </p>
            )}
          </div>
        ))}

        {adding ? (
          <div className="rounded-xl bg-surface-2 p-3 space-y-2">
            <Field label={de ? 'Objekt' : 'Booking object'}>
              <Select value={draftUnit} onChange={(e) => setDraftUnit(e.target.value)}>
                {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
            </Field>
            <Field label={de ? 'Name' : 'Name'} hint={de ? 'Wofür der Link steht, z. B. „Booking.com".' : 'What the link stands for, e.g. “Booking.com”.'}>
              <Input value={draftLabel} onChange={(e) => setDraftLabel(e.target.value)} placeholder="Booking.com" />
            </Field>
            <Field
              label={de ? 'Adresse des Portalkalenders' : 'Portal calendar address'}
              hint={de
                ? 'Der iCal-Export-Link aus dem Portal. Beginnt mit https:// oder webcal://.'
                : 'The iCal export link from the portal. Starts with https:// or webcal://.'}
            >
              <Input
                value={draftUrl}
                onChange={(e) => setDraftUrl(e.target.value)}
                placeholder="https://ical.booking.com/v1/export?t=…"
                inputMode="url"
              />
            </Field>
            <div className="flex gap-2">
              <Button variant="primary" size="sm" disabled={busy} onClick={addImport}>
                <Check size={14} /> {de ? 'Hinzufügen' : 'Add'}
              </Button>
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => setAdding(false)}>
                {de ? 'Abbrechen' : 'Cancel'}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            size="sm"
            disabled={busy || units.length === 0}
            onClick={() => { setDraftUnit(units[0]?.id ?? ''); setAdding(true) }}
          >
            <Plus size={14} /> {de ? 'Portalkalender einlesen' : 'Add portal calendar'}
          </Button>
        )}
      </section>

      {/* ------------------------------------------------------- export */}

      <section className="space-y-2 border-t border-border pt-3">
        <h3 className="text-sm font-medium">{de ? 'Bereitstellen' : 'Export'}</h3>
        <p className="text-xs text-muted">
          {de
            ? 'Diesen Link im Portal als Kalender-Import hinterlegen. Er enthält nur Zeiträume und das Wort „Belegt" — keine Gästenamen, keine Preise. Wer den Link hat, sieht Ihre Belegung: nur dorthin geben, wo er hingehört.'
            : 'Add this link in the portal as a calendar import. It carries date ranges and the word “Booked” — no guest names, no prices. Anyone holding the link can see your occupancy, so only give it out where it belongs.'}
        </p>

        {units.map((u) => {
          const feed = exports_.find((f) => f.unit_id === u.id)
          return (
            <div key={u.id} className="rounded-xl bg-surface-2 p-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{u.name}</span>
                {feed
                  ? <CopyButton value={exportUrl(feed.token)} de={de} />
                  : (
                    <Button size="sm" disabled={busy} onClick={() => createExport(u.id, u.name)}>
                      <Link2 size={14} /> {de ? 'Link erzeugen' : 'Create link'}
                    </Button>
                  )}
              </div>
              {feed && (
                <>
                  <p className="text-xs text-muted break-all font-mono">{exportUrl(feed.token)}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted flex-1">
                      {de ? 'Zuletzt abgerufen: ' : 'Last fetched: '}{whenText(feed.last_sync_at, de)}
                    </span>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => rotate(feed)}>
                      {de ? 'Link zurücksetzen' : 'Reset link'}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => removeFeed(feed)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </>
              )}
            </div>
          )
        })}
      </section>
    </Card>
  )
}
